import React, { useEffect, useState } from 'react';
import { settingsAPI } from '../../services/api';
import { Spinner, Button, Card } from '../../components/ui';
import toast from 'react-hot-toast';

const NAVY = '#1B2A6B';
const inp = {
  width: '100%', padding: '10px 12px', fontSize: 14, border: '1.5px solid #e2e8f0',
  borderRadius: 8, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box',
};
const lbl = { fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 4 };

function Toggle({ label, sublabel, checked, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ flex: 1, paddingRight: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{label}</div>
        {sublabel && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sublabel}</div>}
      </div>
      <div
        onClick={() => onChange(!checked)}
        style={{
          width: 44, height: 24, borderRadius: 12, cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
          background: checked ? NAVY : '#e2e8f0', position: 'relative',
        }}>
        <div style={{
          position: 'absolute', top: 3, left: checked ? 23 : 3,
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
        }} />
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 style={{ fontSize: 14, fontWeight: 800, color: NAVY, margin: '0 0 16px', paddingBottom: 8, borderBottom: `2px solid #f1f5f9` }}>
      {children}
    </h3>
  );
}

const DEFAULT = {
  system_from_email: 'noreply@novaeralabs.us',
  system_from_name: 'Nova Era Labs CRM',
  reply_to_email: 'support@novaeralabs.us',
  use_sender_email_for_outreach: false,
  outreach_from_name_format: 'Full Name, Nova Era Labs',
  send_lead_assigned_email: true,
  send_followup_reminder_email: true,
  send_stale_lead_email: true,
  send_demo_reminder_email: true,
  send_task_due_email: true,
  send_stage_change_email: true,
  send_scholarship_result_email: true,
  send_mou_followup_email: true,
  student_confirmation_from_email: 'admissions@novaeralabs.us',
  student_confirmation_from_name: 'Nova Era Labs Admissions',
  franchise_inquiry_from_email: 'partnerships@novaeralabs.us',
  franchise_inquiry_from_name: 'Nova Era Labs Partnerships',
  email_footer_text: 'This email was sent by Nova Era Labs CRM. Vivasvat Revolutions Limited, BKC Mumbai. For support contact support@novaeralabs.us',
  email_signature_template: '<p>Best regards,<br/>{sender_name}<br/>Nova Era Labs<br/>www.novaeralabs.us<br/>support@novaeralabs.us</p>',
  sendgrid_api_key_configured: false,
  last_test_email_sent_at: null,
  last_test_email_recipient: null,
  last_test_email_status: null,
};

export default function EmailSettingsPage() {
  const [settings, setSettings] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    settingsAPI.getEmailSettings()
      .then(r => setSettings({ ...DEFAULT, ...r.data?.data }))
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await settingsAPI.updateEmailSettings(settings);
      setSettings({ ...DEFAULT, ...res.data?.data });
      toast.success('Email settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleTestEmail = async () => {
    if (!testEmail) { toast.error('Enter a recipient email address'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      await settingsAPI.sendTestEmail(testEmail);
      setTestResult({ success: true, message: `Test email sent to ${testEmail}` });
      set('last_test_email_status', 'success');
      set('last_test_email_recipient', testEmail);
      set('last_test_email_sent_at', new Date().toISOString());
    } catch (err) {
      const msg = err.response?.data?.message || 'Test email failed';
      setTestResult({ success: false, message: msg });
    } finally { setTesting(false); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div>;

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: NAVY, margin: 0 }}>Email Configuration</h1>
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>Configure SendGrid email delivery for notifications and outreach</p>
      </div>

      {/* Section 1: SendGrid Status */}
      <Card style={{ marginBottom: 20 }}>
        <SectionTitle>SendGrid Status</SectionTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20,
            background: settings.sendgrid_api_key_configured ? '#dcfce7' : '#fee2e2',
            color: settings.sendgrid_api_key_configured ? '#15803d' : '#dc2626',
            fontWeight: 700, fontSize: 13,
          }}>
            <span>{settings.sendgrid_api_key_configured ? '✅' : '❌'}</span>
            {settings.sendgrid_api_key_configured ? 'SendGrid Connected' : 'SendGrid Not Configured'}
          </div>
        </div>
        {!settings.sendgrid_api_key_configured && (
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '14px 16px', fontSize: 13, color: '#9a3412' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 700 }}>⚙️ Setup Required</p>
            <p style={{ margin: '0 0 8px' }}>Add <code style={{ background: '#fee2e2', padding: '2px 6px', borderRadius: 4 }}>SENDGRID_API_KEY</code> to your Heroku Config Vars to enable email delivery.</p>
            <a href="https://dashboard.heroku.com/apps" target="_blank" rel="noopener noreferrer"
              style={{ color: '#2563eb', fontWeight: 600 }}>Open Heroku Dashboard →</a>
          </div>
        )}
      </Card>

      {/* Section 2: From Address */}
      <Card style={{ marginBottom: 20 }}>
        <SectionTitle>From Address Configuration</SectionTitle>
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>System From Email</label>
              <input style={inp} value={settings.system_from_email} onChange={e => set('system_from_email', e.target.value)} placeholder="noreply@novaeralabs.us" />
            </div>
            <div>
              <label style={lbl}>System From Name</label>
              <input style={inp} value={settings.system_from_name} onChange={e => set('system_from_name', e.target.value)} placeholder="Nova Era Labs CRM" />
            </div>
          </div>
          <div>
            <label style={lbl}>Reply-To Email</label>
            <input style={inp} value={settings.reply_to_email} onChange={e => set('reply_to_email', e.target.value)} placeholder="support@novaeralabs.us" />
          </div>

          <div>
            <Toggle
              label="Use Sender Email for Outreach"
              sublabel="When ON, emails sent via AI Studio come from the team member's own email. When OFF, all emails come from the system address with reply-to set to the sender."
              checked={settings.use_sender_email_for_outreach}
              onChange={v => set('use_sender_email_for_outreach', v)}
            />
          </div>

          <div>
            <label style={lbl}>Outreach From Name Format</label>
            <select style={{ ...inp, cursor: 'pointer' }} value={settings.outreach_from_name_format} onChange={e => set('outreach_from_name_format', e.target.value)}>
              <option value="First Name only">First Name only (e.g. "Rahul")</option>
              <option value="Full Name">Full Name (e.g. "Rahul Sharma")</option>
              <option value="Full Name, Nova Era Labs">Full Name + Nova Era Labs (e.g. "Rahul Sharma, Nova Era Labs")</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Section 3: Student and Franchise Emails */}
      <Card style={{ marginBottom: 20 }}>
        <SectionTitle>Student & Franchise Confirmation Emails</SectionTitle>
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ ...lbl, color: '#1d4ed8' }}>🎓 Student Confirmation From Email</label>
              <input style={inp} value={settings.student_confirmation_from_email} onChange={e => set('student_confirmation_from_email', e.target.value)} />
            </div>
            <div>
              <label style={{ ...lbl, color: '#1d4ed8' }}>Student Confirmation From Name</label>
              <input style={inp} value={settings.student_confirmation_from_name} onChange={e => set('student_confirmation_from_name', e.target.value)} />
            </div>
          </div>
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ ...lbl, color: '#7c3aed' }}>🤝 Franchise Inquiry From Email</label>
              <input style={inp} value={settings.franchise_inquiry_from_email} onChange={e => set('franchise_inquiry_from_email', e.target.value)} />
            </div>
            <div>
              <label style={{ ...lbl, color: '#7c3aed' }}>Franchise Inquiry From Name</label>
              <input style={inp} value={settings.franchise_inquiry_from_name} onChange={e => set('franchise_inquiry_from_name', e.target.value)} />
            </div>
          </div>
        </div>
      </Card>

      {/* Section 4: Notification Toggles */}
      <Card style={{ marginBottom: 20 }}>
        <SectionTitle>Email Notification Toggles</SectionTitle>
        {[
          { key: 'send_lead_assigned_email',      label: 'Lead Assigned Notification',       sublabel: 'Sent to team member when a lead is assigned to them' },
          { key: 'send_followup_reminder_email',  label: 'Follow-up Reminder',               sublabel: 'Sent before a scheduled follow-up is due' },
          { key: 'send_stale_lead_email',         label: 'Stale Lead Alert',                 sublabel: 'Sent when a lead has had no activity for 7+ days' },
          { key: 'send_demo_reminder_email',      label: 'Demo Reminder (24h & 1h)',         sublabel: 'Sent to attendees before a scheduled demo' },
          { key: 'send_task_due_email',           label: 'Task Due Alert',                   sublabel: 'Sent when a task deadline is approaching' },
          { key: 'send_stage_change_email',       label: 'Stage Change Notification',        sublabel: 'Sent to assigned user when lead stage changes' },
          { key: 'send_scholarship_result_email', label: 'Scholarship Result Email',         sublabel: 'Sent to student with scholarship eligibility result' },
          { key: 'send_mou_followup_email',       label: 'MoU Follow-up Reminder',          sublabel: 'Sent to RCM when MoU has been outstanding for 7+ days' },
        ].map(({ key, label, sublabel }) => (
          <Toggle key={key} label={label} sublabel={sublabel}
            checked={settings[key]} onChange={v => set(key, v)} />
        ))}
      </Card>

      {/* Section 5: Signature & Footer */}
      <Card style={{ marginBottom: 20 }}>
        <SectionTitle>Email Signature & Footer</SectionTitle>
        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <label style={lbl}>Email Signature Template (HTML)</label>
            <p style={{ fontSize: 11, color: '#94a3b8', margin: '0 0 6px' }}>Use <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>{'{sender_name}'}</code> as a placeholder for the sender's name</p>
            <textarea rows={5} style={{ ...inp, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
              value={settings.email_signature_template}
              onChange={e => set('email_signature_template', e.target.value)} />
          </div>

          {/* Signature Preview */}
          {settings.email_signature_template && (
            <div>
              <label style={{ ...lbl, color: '#94a3b8' }}>PREVIEW</label>
              <div style={{ border: '1px dashed #e2e8f0', borderRadius: 8, padding: '12px', background: '#fafafa', fontSize: 13 }}
                dangerouslySetInnerHTML={{ __html: settings.email_signature_template.replace('{sender_name}', 'Your Name') }} />
            </div>
          )}

          <div>
            <label style={lbl}>Email Footer Text (plain text)</label>
            <textarea rows={3} style={{ ...inp, resize: 'vertical' }}
              value={settings.email_footer_text}
              onChange={e => set('email_footer_text', e.target.value)} />
          </div>
        </div>
      </Card>

      {/* Section 6: Test Email */}
      <Card style={{ marginBottom: 24 }}>
        <SectionTitle>Send Test Email</SectionTitle>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>
          Verify that SendGrid is configured correctly by sending a test email.
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Recipient Email Address</label>
            <input style={inp} type="email" placeholder="you@example.com" value={testEmail} onChange={e => setTestEmail(e.target.value)} />
          </div>
          <button onClick={handleTestEmail} disabled={testing || !settings.sendgrid_api_key_configured}
            style={{
              padding: '10px 20px', background: testing ? '#94a3b8' : (!settings.sendgrid_api_key_configured ? '#e2e8f0' : NAVY),
              color: testing || !settings.sendgrid_api_key_configured ? '#94a3b8' : '#fff',
              border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: testing ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            }}>
            {testing ? 'Sending…' : '📧 Send Test'}
          </button>
        </div>

        {testResult && (
          <div style={{
            marginTop: 12, padding: '10px 14px', borderRadius: 8,
            background: testResult.success ? '#dcfce7' : '#fee2e2',
            color: testResult.success ? '#15803d' : '#dc2626',
            fontSize: 13, fontWeight: 600,
          }}>
            {testResult.success ? '✅' : '❌'} {testResult.message}
          </div>
        )}

        {settings.last_test_email_sent_at && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#94a3b8' }}>
            Last test: <strong>{new Date(settings.last_test_email_sent_at).toLocaleString('en-IN')}</strong>
            {' · '}{settings.last_test_email_recipient}
            {' · '}<span style={{ color: settings.last_test_email_status === 'success' ? '#15803d' : '#dc2626', fontWeight: 700 }}>{settings.last_test_email_status}</span>
          </div>
        )}
      </Card>

      {/* Save Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : '💾 Save Email Settings'}
        </Button>
      </div>
    </div>
  );
}
